import React from 'react';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import { Item2DescriptionData } from '../schemas/rql';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { RCToggledContent, RCAlertBox } from '@reverbdotcom/cadence/components';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { Item2Prop65Warning, Item2Prop65WarningFragment } from './item2_prop65_warning';
import { TruncationContainer } from '@reverbdotcom/commons/src/components/truncation_container';

const TRUNCATION_CONTAINER_REM_HEIGHT = 20;

const trackClick = (collapsed, listing) => {
  trackEvent({
    eventName: MParticleEventName.ClickedToggledContent,
    componentName: 'Item2Description',
    interactionType: collapsed ? 'collapse' : 'expand',
    listing,
  });
};

export const Item2DescriptionFragment = gql`
  fragment Item2DescriptionData on Listing {
    _id
    id
    description
    soldAsIs
    ...Item2Prop65WarningData
  }
  ${Item2Prop65WarningFragment}
`;

interface IProps {
  listing: Item2DescriptionData.Fragment;
}

export function Item2Description({ listing }: IProps) {
  if (!listing) return null;

  return (
    <div className="item2-description">
      <RCToggledContent
        heading={I18n.t('discovery.item2Description.about')}
        lockOpen={false}
        defaultOpen
        id="description"
        onToggle={(collapsed) => trackClick(collapsed, listing)}
      >
        <TruncationContainer
          remMaxHeight={TRUNCATION_CONTAINER_REM_HEIGHT}
          trackingName="Item2Description"
        >
          <SanitizedRender
            html={listing.description}
            htmlTag="div"
            className="item2-description__content"
            truncationHeight={null}
          />
        </TruncationContainer>
        <Item2Prop65Warning
          listing={listing}
        />
        {listing.soldAsIs &&
          <SoldAsDescribedMessage />
        }
      </RCToggledContent>
    </div>
  );
}

function SoldAsDescribedMessage() {
  const soldAsIsHelpCenterArticleId = '360003063773';

  return (
    <div className="scaling-mt-2">
      <RCAlertBox type={'warning'} small>
        <h4 className="size-120 weight-bold mb-1">
          {I18n.t('discovery.item2Description.asDescribed.title')}
        </h4>
        <p className="mb-0">
          {I18n.t('discovery.item2Description.asDescribed.body')}
          <CoreLink
            target="_blank"
            rel="noreferrer"
            className="ml-space"
            to={useHelpCenterArticleUrl(soldAsIsHelpCenterArticleId)}
          >
            {I18n.t('discovery.item2Description.asDescribed.linkText')}
          </CoreLink>
        </p>
      </RCAlertBox>
    </div>
  );
}
